import { useContext } from "react";
import { useReactiveVar } from "@apollo/client";
import { branchesLength } from "@me-team/host/main/branches/state";
import { CompanyAccess, PermissionScope } from "@me-team/host/main/graphql/types";
import { AccessContext } from "@me-team/host/src/components/App/App";
import AccessService from "@me-team/host/services/AccessService";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

export const useLogbookAccess = () => {
    const { isReady: isAccessContextReady } = useContext(AccessContext);
    const totalActiveBranches = useReactiveVar(branchesLength);
    const accessService = AccessService.getInstance();

    const canViewLogbookAll = useRoleAccess(CompanyAccess.LogbookView, {
        scope: PermissionScope.All,
    });
    const canViewLogbookSelf = useRoleAccess(CompanyAccess.LogbookView, {
        scope: PermissionScope.Self,
    });
    const canCreateLogbookBookingAll = useRoleAccess(CompanyAccess.LogbookBookingCreate, {
        scope: PermissionScope.All,
    });
    const canCreateLogbookBookingSelf = useRoleAccess(CompanyAccess.LogbookBookingCreate, {
        scope: PermissionScope.Self,
    });
    const canEditLogbookBookingAll = useRoleAccess(CompanyAccess.LogbookBookingEdit, {
        scope: PermissionScope.All,
    });
    const canEditLogbookBookingSelf = useRoleAccess(CompanyAccess.LogbookBookingEdit, {
        scope: PermissionScope.Self,
    });
    const canEditLogbookPopoverBookingStatusAll = useRoleAccess(CompanyAccess.LogbookPopoverBookingStatusEdit, {
        scope: PermissionScope.All,
    });
    const canEditLogbookPopoverBookingStatusSelf = useRoleAccess(CompanyAccess.LogbookPopoverBookingStatusEdit, {
        scope: PermissionScope.Self,
    });
    const canWriteOffLogbookConsumables = useRoleAccess(CompanyAccess.LogbookConsumablesWriteOff);
    const canCreateScheduleAll = useRoleAccess(CompanyAccess.ScheduleCreate, {
        scope: PermissionScope.All,
    });
    const canCreateScheduleSelf = useRoleAccess(CompanyAccess.ScheduleCreate, {
        scope: PermissionScope.Self,
    });
    const canCreateCustomDayAll = useRoleAccess(CompanyAccess.CustomDayCreate, {
        scope: PermissionScope.All,
    });
    const canCreateCustomDaySelf = useRoleAccess(CompanyAccess.CustomDayCreate, {
        scope: PermissionScope.Self,
    });
    const canCreateVacationAll = useRoleAccess(CompanyAccess.VacationCreate, {
        scope: PermissionScope.All,
    });
    const canCreateVacationSelf = useRoleAccess(CompanyAccess.VacationCreate, {
        scope: PermissionScope.Self,
    });
    const canOpenEmployeeServicesAll = useRoleAccess(CompanyAccess.EmployeeServicesEdit, {
        scope: PermissionScope.All,
    });
    const canOpenEmployeeServicesSelf = useRoleAccess(CompanyAccess.EmployeeServicesEdit, {
        scope: PermissionScope.Self,
    });
    const canManageCompanyEmployees = useRoleAccess(CompanyAccess.AccessManageCompanyEmployees);

    const isLogbookAccessPending =
        !isAccessContextReady || !accessService.isRoleAccessReady() || accessService.isRoleAccessLoading();
    const canViewLogbookPage = canViewLogbookAll || canViewLogbookSelf;
    const shouldShowOnlyOwnLogbook = canViewLogbookSelf && !canViewLogbookAll;
    const canShowAddEmployeeFromAnotherBranchAction = canManageCompanyEmployees && totalActiveBranches > 1;

    return {
        canViewLogbookAll,
        canViewLogbookSelf,
        canViewLogbookPage,
        canCreateLogbookBookingAll,
        canCreateLogbookBookingSelf,
        canEditLogbookBookingAll,
        canEditLogbookBookingSelf,
        canEditLogbookPopoverBookingStatusAll,
        canEditLogbookPopoverBookingStatusSelf,
        canWriteOffLogbookConsumables,
        canCreateScheduleAll,
        canCreateScheduleSelf,
        canCreateCustomDayAll,
        canCreateCustomDaySelf,
        canCreateVacationAll,
        canCreateVacationSelf,
        canOpenEmployeeServicesAll,
        canOpenEmployeeServicesSelf,
        canManageCompanyEmployees,
        canShowAddEmployeeFromAnotherBranchAction,
        shouldShowOnlyOwnLogbook,
        logbookAccessContract: {
            isLogbookAccessPending,
            canViewLogbookPage,
        },
    };
};
